import { Disclosure } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/solid';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import useLocale from 'next-translate/useTranslation';
import React, { useState } from 'react';

import clsxm from '@/lib/clsxm';
import { loginHrefForPath, signupHrefForPath } from '@/lib/presetFromPath';

import { LanguageSelector } from '@/components/layout/LanguageSelector';
import { ThemeSelector } from '@/components/layout/ThemeSelector';

import { useUser } from '@/utils/useUser';

const FlyOutMenuAgents = dynamic(() => import('../atoms/FlyOutMenuAgents'), {
  ssr: false,
});
const FlyoutMenuFullWidth = dynamic(
  () => import('../atoms/FlyoutMenuFullWidth'),
  { ssr: false },
);
const FlyOutMenuVideo = dynamic(() => import('../atoms/FlyOutMenuVideo'), {
  ssr: false,
});
const FlyOutMenuAudio = dynamic(() => import('../atoms/FlyOutMenuAudio'), {
  ssr: false,
});
const FlyoutSimpleMenuItem = dynamic(
  () => import('../atoms/FlyoutSimpleMenuItem'),
  { ssr: false },
);
const ButtonLink = dynamic(() => import('../links/ButtonLink'));

// Mobile navigation groups. These mirror the desktop flyout menus so that the
// mobile drawer offers the same grouping, but collapsed into accordions.
const mobileNavGroups = [
  {
    label: 'Agents',
    items: [
      { name: 'MARKy Agent', href: '/marky' },
      // Also under Video — listed in both, like the desktop flyouts.
      { name: 'Video Agent', href: '/create-video' },
      { name: 'Bots', href: '/chatbots' },
      { name: 'Workflows', href: '/ai-workflows' },
      { name: 'Content Templates', href: '/templates' },
      { name: 'Brand Voice', href: '/voices' },
      { name: 'MCP Server', href: '/mcp' },
    ],
  },
  {
    label: 'Images',
    items: [
      { name: 'AI Image Generator', href: '/ai-image-generator' },
      {
        name: 'Nano Banana Models',
        href: '/ai-image-generator/nano-banana',
      },
      {
        name: 'Seedream 5.0 Pro',
        href: '/ai-image-generator/seedream-5-0-pro',
      },
      { name: 'Photo Studio', href: '/ai-photo-generator' },
      { name: 'Headshots', href: '/ai-headshot-generator' },
      {
        name: 'AI Interior Design Generator',
        href: '/ai-interior-design-generator',
      },
      { name: 'Free AI Image Upscaler', href: '/free-tools/upscale-image' },
      {
        name: 'T-Shirt Design Maker',
        href: '/ai-image-generator/t-shirt-design',
      },
      { name: 'Logo Maker', href: '/ai-image-generator/logo' },
    ],
  },
  {
    label: 'Video',
    items: [
      { name: 'Video Agent', href: '/create-video' },
      { name: 'AI Video Generator', href: '/ai-video-generator' },
      { name: 'AI Video Upscaler', href: '/ai-video-upscaler' },
      { name: 'AI Video Extender', href: '/ai-video-extender' },
      { name: 'Edit Video with AI', href: '/edit-video-with-ai' },
      { name: 'Talking Videos', href: '/ai-video-generator/talking' },
      {
        name: 'Make Image Talk',
        href: '/ai-video-generator/make-image-talk',
      },
      { name: 'Seedance 2.5', href: '/ai-video-generator/seedance-2-5' },
      { name: 'MiniMax H3', href: '/ai-video-generator/minimax-h3' },
      { name: 'WAN 3.0', href: '/ai-video-generator/wan-3-0' },
      {
        name: 'Gemini Omni Flash 1.1',
        href: '/ai-video-generator/gemini-omni-1-1-flash',
      },
      { name: 'Motion Control', href: '/ai-video-generator/seedance-2' },
      { name: 'Video Editor', href: '/ai-video-editor' },
      { name: 'AI Animate Image', href: '/ai-animate-image' },
    ],
  },
  {
    label: 'Audio',
    items: [
      { name: 'AI Audio Generator', href: '/ai-audio-generator' },
      { name: 'AI Text-to-Speech', href: '/text-to-speech' },
      { name: 'AI Voice Changer', href: '/voice-changer' },
      { name: 'AI Transcription', href: '/ai-transcription' },
      { name: 'AI Music Generator', href: '/ai-music-generator' },
      { name: 'AI Sound Effects', href: '/ai-sound-effects-generator' },
    ],
  },
  {
    label: 'Resources',
    items: [
      { name: 'Mobile App', href: '/mobile-app' },
      { name: 'Blog', href: '/blog' },
      { name: 'Docs', href: '/docs' },
      { name: 'Pricing', href: '/pricing' },
      { name: "What's New", href: '/changelog' },
      { name: 'Affiliate Program', href: '/affiliates' },
      { name: 'AI Workshops', href: '/ai-workshops' },
    ],
  },
];

interface LandingPageNavigationInnerProps {
  variant: 'default' | 'light';
  user: any;
  forceDark?: boolean;
}

interface LandingPageNavigationProps {
  variant?: 'default' | 'light';
  skipUserContext?: boolean;
  forceDark?: boolean;
}

const LandingPageNavigationWithUser = ({
  variant = 'default',
  forceDark,
}: LandingPageNavigationProps) => {
  const { user } = useUser();
  return (
    <LandingPageNavigationInner
      variant={variant}
      user={user}
      forceDark={forceDark}
    />
  );
};

const LandingPageNavigation = ({
  variant = 'default',
  skipUserContext = false,
  forceDark = false,
}: LandingPageNavigationProps) => {
  if (skipUserContext) {
    return (
      <LandingPageNavigationInner
        variant={variant}
        user={null}
        forceDark={forceDark}
      />
    );
  }
  return (
    <LandingPageNavigationWithUser variant={variant} forceDark={forceDark} />
  );
};

const LandingPageNavigationInner = ({
  variant,
  user,
  forceDark,
}: LandingPageNavigationInnerProps) => {
  const { t } = useLocale('footer');
  const router = useRouter();
  const [isOpened, setIsOpened] = useState(false);

  // Carry the landing page's tool intent into signup/login so the dashboard
  // can deep-link the user into the tool they came for.
  const signupHref = signupHrefForPath(router.asPath);
  const loginHref = loginHrefForPath(router.asPath);

  const ctaLinkClasses =
    variant === 'light'
      ? 'font-medium text-white hover:text-white/80'
      : 'font-medium text-primary-600 hover:text-primary-500';

  const mobileButtonClasses =
    variant === 'light'
      ? 'inline-flex items-center justify-center rounded-md bg-white/10 backdrop-blur-sm p-2 text-white hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white/50'
      : 'inline-flex items-center justify-center rounded-md bg-white p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 dark:bg-slate-800 dark:text-slate-200';

  // Cap the drawer at the viewport height and scroll inside it: the drawer is
  // absolutely positioned inside page sections that clip overflow (e.g. the
  // home page hero), so content taller than the viewport would be cut off.
  const mobileDrawerScrollClasses =
    'max-h-[calc(100vh-1rem)] supports-[height:100dvh]:max-h-[calc(100dvh-1rem)] overflow-y-auto overscroll-contain';

  const mobileDrawerClasses =
    variant === 'light'
      ? `${mobileDrawerScrollClasses} rounded-lg bg-[#0B0F19] shadow-2xl ring-1 ring-white/10 border border-white/10`
      : `${mobileDrawerScrollClasses} rounded-lg bg-white shadow-md ring-1 ring-black ring-opacity-5 dark:bg-slate-900`;

  const mobileHeaderRowClasses =
    variant === 'light'
      ? 'sticky top-0 z-10 flex items-center justify-between bg-[#0B0F19] px-5 pb-2 pt-4'
      : 'sticky top-0 z-10 flex items-center justify-between bg-white px-5 pb-2 pt-4 dark:bg-slate-900';

  const mobileCloseButtonClasses =
    variant === 'light'
      ? 'inline-flex items-center justify-center rounded-md bg-white/10 p-2 text-gray-400 hover:bg-white/20 hover:text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500'
      : 'inline-flex items-center justify-center rounded-md bg-white p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 dark:bg-slate-800 dark:text-slate-200';

  const mobileLinkClasses =
    variant === 'light'
      ? 'block rounded-md px-3 py-2 text-base font-medium text-gray-300 hover:bg-white/10 hover:text-white'
      : 'block rounded-md px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900 dark:text-slate-200 dark:hover:bg-slate-700';

  const mobileSettingsTextClasses =
    variant === 'light' ? 'text-gray-400' : 'text-gray-500';

  const mobileGroupButtonClasses =
    variant === 'light'
      ? 'flex w-full items-center justify-between rounded-md px-3 py-2 text-base font-semibold text-white hover:bg-white/10'
      : 'flex w-full items-center justify-between rounded-md px-3 py-2 text-base font-semibold text-gray-900 hover:bg-gray-50 dark:text-slate-200 dark:hover:bg-slate-700';

  const mobileGroupChevronClasses =
    variant === 'light' ? 'text-gray-400' : 'text-gray-400 dark:text-slate-500';

  const mobileLoginClasses =
    variant === 'light'
      ? 'block w-full bg-white/5 px-5 py-3 text-center font-medium text-white hover:bg-white/10'
      : 'block w-full bg-gray-50 px-5 py-3 text-center font-medium text-primary-700 hover:bg-gray-100 dark:bg-slate-800';

  const mobileDashboardClasses =
    variant === 'light'
      ? 'block w-full bg-white/5 px-5 py-3 text-center font-medium text-white hover:bg-white/10'
      : 'block w-full bg-gray-50 px-5 py-3 text-center font-medium text-primary-600 hover:bg-gray-100';

  return (
    <div className={forceDark ? 'dark' : ''}>
      {/* <Banner /> */}
      <div className='relative px-4 pt-6 sm:px-6 2xl:px-8'>
        <nav
          className='relative flex items-center justify-between sm:h-10 xl:justify-start'
          aria-label='Global'
        >
          <div className='flex flex-shrink-0 flex-grow items-center'>
            <div className='flex w-full items-center justify-between xl:w-auto'>
              <Link href='/' passHref>
                <Image
                  src='/images/marky.png'
                  width='250'
                  height='36'
                  priority={true}
                  className='text-2xl font-extrabold text-primary-700'
                  alt='Easy-Peasy.ai logo'
                />
              </Link>
              <div className='-mr-2 flex items-center xl:hidden'>
                <button
                  type='button'
                  className={mobileButtonClasses}
                  aria-expanded='false'
                  onClick={() => setIsOpened(!isOpened)}
                >
                  <span className='sr-only'>Open main menu</span>
                  <svg
                    className='h-6 w-6'
                    xmlns='http://www.w3.org/2000/svg'
                    fill='none'
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                    aria-hidden='true'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth='2'
                      d='M4 6h16M4 12h16M4 18h16'
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
          <div className='hidden xl:flex xl:items-center xl:space-x-6 2xl:ml-10 2xl:space-x-8'>
            <FlyOutMenuAgents variant={variant} />

            <div className='inline-flex'>
              <FlyoutMenuFullWidth variant={variant} />
            </div>

            <FlyOutMenuVideo variant={variant} />

            <FlyOutMenuAudio variant={variant} />

            <FlyoutSimpleMenuItem variant={variant} />

            <Link
              href='/mcp'
              className={
                variant === 'light'
                  ? 'font-medium leading-8 text-white/90 hover:text-white'
                  : 'font-medium leading-8 text-gray-500 hover:text-gray-900 dark:text-slate-400 dark:hover:text-slate-700'
              }
            >
              {t('MCP')}
            </Link>

            <Link
              href='/pricing'
              className={
                variant === 'light'
                  ? 'font-medium leading-8 text-white/90 hover:text-white'
                  : 'font-medium leading-8 text-gray-500 hover:text-gray-900 dark:text-slate-400 dark:hover:text-slate-700'
              }
            >
              {t('Pricing')}
            </Link>

            <div className='inline-flex align-middle'>
              <LanguageSelector className='relative mr-3' variant={variant} />
              <ThemeSelector className='relative' variant={variant} />
            </div>

            {!user ? (
              <>
                <Link href={loginHref} className={ctaLinkClasses}>
                  {t('Log in')}
                </Link>
                <ButtonLink
                  variant={variant === 'light' ? 'light' : 'dark'}
                  href={signupHref}
                >
                  <span>{t('Sign up')}</span>
                </ButtonLink>
              </>
            ) : (
              <Link href='/dashboard' className={ctaLinkClasses}>
                {t('Go to dashboard')}
              </Link>
            )}
          </div>
        </nav>
      </div>

      <div
        className={clsxm(
          isOpened ? '' : 'hidden',
          ' absolute inset-x-0 top-0 z-[999] origin-top-right transform p-2 transition xl:hidden',
        )}
      >
        <div className={mobileDrawerClasses}>
          <div className={mobileHeaderRowClasses}>
            <div>
              <div className='inline-flex align-middle'>
                <span className={mobileSettingsTextClasses}>
                  {t('Settings')}
                </span>
                <LanguageSelector className='relative ml-2 mr-3' />

                <ThemeSelector className=' relative' />
              </div>
            </div>
            <div className='-mr-2'>
              <button
                onClick={() => setIsOpened(!isOpened)}
                type='button'
                className={mobileCloseButtonClasses}
              >
                <span className='sr-only'>{t('Close main menu')}</span>
                <svg
                  className='h-6 w-6'
                  xmlns='http://www.w3.org/2000/svg'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                  aria-hidden='true'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='2'
                    d='M6 18L18 6M6 6l12 12'
                  />
                </svg>
              </button>
            </div>
          </div>
          <div className='px-4 pb-2 pt-3'>
            {!user ? (
              <div className='flex gap-2'>
                <Link
                  href={loginHref}
                  className={clsxm(
                    mobileLoginClasses,
                    'flex-1 rounded-lg py-2.5',
                  )}
                >
                  {t('Log in')}
                </Link>
                <ButtonLink
                  className='flex-1 text-center rounded-lg'
                  variant='dark'
                  href={signupHref}
                >
                  {t('Sign up for free')}
                </ButtonLink>
              </div>
            ) : (
              <Link
                href='/dashboard'
                className={clsxm(
                  mobileDashboardClasses,
                  'block rounded-lg py-2.5',
                )}
              >
                {t('Go to dashboard')}
              </Link>
            )}
          </div>
          <div className='space-y-1 px-2 pb-3 pt-2'>
            {mobileNavGroups.map((group) => (
              <Disclosure as='div' key={group.label}>
                {({ open }) => (
                  <>
                    <Disclosure.Button className={mobileGroupButtonClasses}>
                      <span>{t(group.label)}</span>
                      <ChevronDownIcon
                        className={clsxm(
                          'h-5 w-5 transition-transform duration-200',
                          mobileGroupChevronClasses,
                          open && 'rotate-180',
                        )}
                        aria-hidden='true'
                      />
                    </Disclosure.Button>
                    {/* unmount={false} keeps the links in the server-rendered
                        HTML (internal linking / SEO) instead of mounting them
                        only when the group is expanded. */}
                    <Disclosure.Panel
                      unmount={false}
                      className='space-y-1 pb-2 pl-3'
                    >
                      {group.items.map((item) => (
                        <Link
                          key={item.href}
                          href={item.href}
                          className={mobileLinkClasses}
                          onClick={() => setIsOpened(false)}
                        >
                          {t(item.name)}
                        </Link>
                      ))}
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPageNavigation;
